import React, { useEffect, useState } from 'react';
import { useAppContext } from '../AppContext';
import { collection, query, where, onSnapshot, limit, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions'; // Import httpsCallable directly
import * as LucideIcons from 'lucide-react';
import { useSpring, animated } from '@react-spring/web';

// ProgressBar component for animated scores
const ProgressBar = ({ label, score, color }) => {
    const { width } = useSpring({
        width: (score / 10) * 100,
        from: { width: 0 },
        config: { duration: 1000 },
    });

    return (
        <div className="mb-1">
            <div className="flex justify-between text-xs text-gray-300">
                <span>{label}</span>
                <span>{score.toFixed(1)}/10</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
                <animated.div
                    className="h-2 rounded-full"
                    style={{
                        width: width.to(w => `${w}%`),
                        backgroundColor: color,
                    }}
                />
            </div>
        </div>
    );
};

// Custom hook to fetch active minigame data
const useMinigameData = () => {
    const { db, appId } = useAppContext();
    const [minigame, setMinigame] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!db) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const q = query(
            collection(db, `minigames`),
            where("status", "in", ["active", "completed"]), // Listen for both active and completed
            orderBy("startTime", "desc"),
            limit(1)
        );

        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                if (!snapshot.empty) {
                    const activeMinigame = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
                    setMinigame(activeMinigame);
                } else {
                    setMinigame(null);
                }
                setLoading(false);
            },
            (err) => {
                console.error("Error fetching active minigame:", err);
                setError(err);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [db, appId]);

    return { minigame, loading, error };
};

// Custom hook to fetch minigame history data (moved from MinigameHistory.js)
const useMinigameHistory = (limitCount = 10) => {
    const { db, appId } = useAppContext();
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!db) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const q = query(
            collection(db, `minigames`),
            orderBy("completedTime", "desc"),
            limit(limitCount)
        );

        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                const fetchedHistory = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setHistory(fetchedHistory);
                setLoading(false);
            },
            (err) => {
                console.error("Error fetching minigame history:", err);
                setError(err);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [db, appId, limitCount]);

    return { history, loading, error };
};

function MinigamePage() {
    const { minigame, loading: activeLoading, error: activeError } = useMinigameData();
    const { history, loading: historyLoading, error: historyError } = useMinigameHistory();
    const { appFunctions } = useAppContext();
    const [startingShowdown, setStartingShowdown] = useState(false);
    const [activeTab, setActiveTab] = useState('activeShowdown');

    const handleStartShowdown = async () => {
        setStartingShowdown(true);
        try {
            const startShowdownCallable = httpsCallable(appFunctions, 'startSynapticShowdown');
            const result = await startShowdownCallable();
            if (result.data.success) {
                console.log("Showdown started:", result.data.minigameId);
                setActiveTab('activeShowdown'); // Switch to active showdown tab
            } else {
                console.error("Failed to start showdown:", result.data.message);
            }
        } catch (err) {
            console.error("Error calling startSynapticShowdown:", err);
        } finally {
            setStartingShowdown(false);
        }
    };

    const renderActiveShowdown = () => {
        if (activeLoading) {
            return <div className="text-white text-center p-4">Loading Synaptic Showdown...</div>;
        }
    
        if (activeError) {
            return <div className="text-red-500 text-center p-4">Error: {activeError.message}</div>;
        }
    
        if (!minigame) {
            return (
                <div className="flex flex-col items-center justify-center p-8 bg-gray-900/70 backdrop-blur-sm rounded-lg shadow-lg text-white max-w-md mx-auto mt-10">
                    <LucideIcons.BrainCircuit size={64} className="text-blue-400 mb-4 animate-pulse" />
                    <h2 className="text-2xl font-bold mb-4">Synaptic Showdown</h2>
                    <p className="text-center mb-6">No active showdown. Be the first to initiate a challenge for the collective minds!</p>
                    <button
                        onClick={handleStartShowdown}
                        disabled={startingShowdown}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded-full transition duration-300 ease-in-out transform hover:scale-105"
                    >
                        {startingShowdown ? 'Initiating...' : 'Start New Showdown'}
                    </button>
                </div>
            );
        }
    
        const sortedDreamWeaves = [...minigame.participatingDreamWeaves].sort((a, b) => b.score - a.score);
        const winnerId = minigame.status === 'completed' ? sortedDreamWeaves[0]?.id : null;
    
        const backgroundClass = minigame.status === 'active'
            ? 'bg-gradient-to-br from-gray-900 to-blue-900 animate-gradient-pulse'
            : minigame.status === 'completed'
                ? 'bg-gradient-to-br from-gray-900 to-green-900'
                : 'bg-gray-900/70';

        return (
            <div className={`p-4 sm:p-6 lg:p-8 backdrop-blur-sm rounded-lg shadow-xl text-white max-w-4xl mx-auto mt-10 border border-blue-700 ${backgroundClass}`}>
                <h2 className="text-3xl font-extrabold text-center text-blue-300 mb-6">Synaptic Showdown: Pattern Synthesis Challenge</h2>
    
                <div className="mb-8 p-4 bg-gray-800 rounded-md border border-gray-700">
                    <h3 className="text-xl font-semibold text-blue-200 mb-3">Challenge Pattern: {minigame.pattern.id}</h3>
                    <p className="text-lg mb-2">{minigame.pattern.description}</p>
                    {minigame.pattern.data && (
                        <p className="text-sm text-gray-400">Data: {JSON.stringify(minigame.pattern.data)}</p>
                    )}
                </div>
    
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {sortedDreamWeaves.map((dw, index) => (
                        <div
                            key={dw.id}
                            className={`bg-gray-800 p-5 rounded-lg border flex flex-col justify-between ${dw.id === winnerId ? 'border-yellow-400 shadow-lg shadow-yellow-500/50 animate-glow-pulse' : 'border-gray-700'}`}
                        >
                            <div>
                                <h4 className={`text-lg font-bold mb-2 flex items-center ${dw.evaluationStatus === 'in_progress' ? 'text-yellow-400 animate-pulse' : 'text-purple-300'}`}>
                                    DreamWeave AI #{index + 1}
                                    {dw.evaluationStatus === 'in_progress' && <LucideIcons.Loader size={18} className="ml-2 animate-spin" />}
                                </h4>
                                <p className="text-sm text-gray-400">ID: {dw.id}</p>
                                {dw.nexusId && <p className="text-sm text-gray-400">Nexus: {dw.nexusId}</p>}
                                <p className="mt-2">Status: <span className={`font-semibold ${dw.evaluationStatus === 'completed' ? 'text-green-400' : dw.evaluationStatus === 'in_progress' ? 'text-yellow-400' : 'text-gray-400'}`}>{dw.evaluationStatus}</span></p>
                                {dw.solution && <p className="mt-2 text-sm">Solution: <span className="text-blue-300 italic">{dw.solution}</span></p>}
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-700">
                                <p className="text-md font-semibold">Total Score: <span className="text-yellow-400">{dw.score.toFixed(2)}</span></p>
                                <div className="text-sm text-gray-400 mt-1">
                                    <ProgressBar label="Coherence" score={dw.coherenceScore} color="#32CD32" />
                                    <ProgressBar label="Creativity" score={dw.creativityScore} color="#FFD700" />
                                    <ProgressBar label="Efficiency" score={dw.efficiencyScore} color="#1E90FF" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
    
                {minigame.status === 'active' && (
                    <p className="text-center text-gray-400 mt-8 text-sm">Evaluation in progress... Results will update automatically.</p>
                )}
                {minigame.status === 'completed' && (
                    <div className="text-center mt-8">
                        <h3 className="text-2xl font-bold text-green-400 flex items-center justify-center">
                            <LucideIcons.Award size={24} className="mr-2 text-yellow-300 animate-bounce" />
                            Showdown Complete!
                            <LucideIcons.Award size={24} className="ml-2 text-yellow-300 animate-bounce" />
                        </h3>
                        <p className="text-lg mt-2">The winning DreamWeave AI is: <span className="text-yellow-300 font-bold">{sortedDreamWeaves[0]?.id}</span> with a score of <span className="text-yellow-300 font-bold">{sortedDreamWeaves[0]?.score.toFixed(2)}</span>!</p>
                    </div>
                )}
            </div>
        );
    };

    const renderMinigameHistory = () => {
        if (historyLoading) {
            return <div className="text-white text-center p-4">Loading Minigame History...</div>;
        }
    
        if (historyError) {
            return <div className="text-red-500 text-center p-4">Error: {historyError.message}</div>;
        }
    
        if (history.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center p-8 bg-gray-900/70 backdrop-blur-sm rounded-lg shadow-lg text-white max-w-md mx-auto mt-10">
                    <LucideIcons.History size={64} className="text-blue-400 mb-4" />
                    <h2 className="text-2xl font-bold mb-4">Minigame History</h2>
                    <p className="text-center">No past minigames found. Start a Synaptic Showdown to see results here!</p>
                </div>
            );
        }
    
        return (
            <div className="p-4 sm:p-6 lg:p-8 bg-gray-900/70 backdrop-blur-sm rounded-lg shadow-xl text-white max-w-4xl mx-auto mt-10 border border-blue-700">
                <h2 className="text-3xl font-extrabold text-center text-blue-300 mb-6">Synaptic Showdown History</h2>
    
                <div className="space-y-6">
                    {history.map((mg) => (
                        <div key={mg.id} className="bg-gray-800 p-5 rounded-lg border border-gray-700">
                            <h3 className="text-xl font-bold text-purple-300 mb-2">Challenge: {mg.pattern.description}</h3>
                            <p className="text-sm text-gray-400 mb-2">Minigame ID: {mg.id}</p>
                            <p className="text-sm text-gray-400 mb-2">Status: <span className={`font-semibold ${mg.status === 'completed' ? 'text-green-400' : 'text-gray-400'}`}>{mg.status}</span></p>
                            {mg.completedTime && (
                                <p className="text-sm text-gray-400">Completed: {new Date(mg.completedTime.toDate()).toLocaleString()}</p>
                            )}
                            
                            {mg.winnerId && (
                                <div className="mt-4 p-3 bg-green-900/30 rounded-md border border-green-700">
                                    <p className="text-lg font-semibold text-green-300">Winner: <span className="text-yellow-300">DreamWeave AI {mg.winnerId}</span></p>
                                    <p className="text-sm text-green-400">Winning Score: {mg.winningScore.toFixed(2)}</p>
                                </div>
                            )}
    
                            <div className="mt-4 pt-4 border-t border-gray-700">
                                <h4 className="text-md font-semibold text-blue-200 mb-2">Participants:</h4>
                                <ul className="list-disc list-inside text-sm text-gray-300">
                                    {mg.participatingDreamWeaves.map((dw, index) => (
                                        <li key={dw.id} className={`mb-1 ${dw.id === mg.winnerId ? 'text-yellow-300 font-bold' : ''}`}>
                                            DreamWeave AI #{index + 1} (ID: {dw.id}) - Score: {dw.score.toFixed(2)}
                                            {dw.solution && <span className="italic text-blue-300"> - Solution: {dw.solution}</span>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 bg-gray-900/70 backdrop-blur-sm rounded-lg shadow-xl text-white max-w-4xl mx-auto mt-10 border border-blue-700">
            <div className="flex justify-center border-b border-gray-700 w-full mb-6">
                <button
                    className={`py-2 px-4 text-sm font-medium ${activeTab === 'activeShowdown' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
                    onClick={() => setActiveTab('activeShowdown')}
                >
                    Active Showdown
                </button>
                <button
                    className={`py-2 px-4 text-sm font-medium ${activeTab === 'history' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
                    onClick={() => setActiveTab('history')}
                >
                    Showdown History
                </button>
            </div>

            <div className="w-full">
                {activeTab === 'activeShowdown' && renderActiveShowdown()}
                {activeTab === 'history' && renderMinigameHistory()}
            </div>
        </div>
    );
}

export default MinigamePage;